/**
 * Shared geo helpers for maps and location validation.
 */

/**
 * @param {unknown} lat
 * @param {unknown} lng
 * @returns {{ lat: number, lng: number } | null}
 */
export function parseLatLng(lat, lng) {
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  const la = Number(lat);
  const lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  if (Math.abs(la) > 90 || Math.abs(lo) > 180) return null;
  // Treat null island / unset placeholders as missing
  if (Math.abs(la) < 1e-5 && Math.abs(lo) < 1e-5) return null;
  return { lat: la, lng: lo };
}

/**
 * ISO 3166-1 alpha-2 → name Google Geocoder resolves reliably (short codes are easy to misread).
 */
export function expandCountryForGeocode(code) {
  if (code == null || code === '') return '';
  const c = String(code).trim().toUpperCase();
  if (c.length !== 2) return String(code).trim();
  const map = { IN: 'India', US: 'United States', GB: 'United Kingdom', AE: 'United Arab Emirates' };
  return map[c] || c;
}

/** India: stored lat/long are often swapped (longitude ~65–99, latitude ~6–37). */
function maybeUnswapIndiaLatLng(lat, lng, country) {
  const c = String(country || '').toUpperCase();
  if (c !== 'IN') return { lat, lng };
  if (lat >= 65 && lat <= 99 && lng >= 6 && lng <= 38) {
    return { lat: lng, lng: lat };
  }
  return { lat, lng };
}

/**
 * Read coordinates from API/context objects that may use latitude/longitude or lat/lng.
 * @param {Record<string, unknown> | null | undefined} obj
 * @returns {{ lat: number, lng: number } | null}
 */
export function parseLatLngFromObject(obj) {
  if (!obj || typeof obj !== 'object') return null;
  const lat = obj.latitude ?? obj.lat;
  const lng = obj.longitude ?? obj.lng ?? obj.lon;
  if (lat == null || lng == null || lat === '' || lng === '') return null;
  let la = Number(lat);
  let lo = Number(lng);
  if (!Number.isFinite(la) || !Number.isFinite(lo)) return null;
  const fixed = maybeUnswapIndiaLatLng(la, lo, obj.country);
  return parseLatLng(fixed.lat, fixed.lng);
}

/**
 * @param {Array<{ id?: unknown, latitude?: unknown, longitude?: unknown, name?: string }>} competitors
 * @returns {Array<{ id: string|number, name: string, lat: number, lng: number }>}
 */
export function competitorsWithValidCoords(competitors) {
  if (!Array.isArray(competitors)) return [];
  const out = [];
  for (let i = 0; i < competitors.length; i += 1) {
    const c = competitors[i];
    const pos = parseLatLngFromObject(c);
    if (!pos) continue;
    const id = c?.id != null ? c.id : `idx-${i}`;
    const rawRate = c?.average_rate;
    const rate =
      rawRate != null && rawRate !== '' && Number.isFinite(Number(rawRate)) ? Number(rawRate) : null;
    out.push({
      id,
      name: (c?.name && String(c.name).trim()) || 'Competitor',
      lat: pos.lat,
      lng: pos.lng,
      rate,
      currency: (c?.currency && String(c.currency).toUpperCase().slice(0, 3)) || 'INR',
    });
  }
  return out;
}
